// Fetch available translations (versions) from the API
export async function fetchAvailableTranslations() {
  const cacheKey = 'iqbible_versions';
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const { data, expiry } = JSON.parse(cached);
      if (!expiry || Date.now() < expiry) {
        return data;
      }
    } catch {}
  }
  const url = `${API_BASE}/GetVersions`;
  const res = await fetch(url, { headers: getHeaders() });
  if (!res.ok) throw new Error('IQBible API error');
  const data = await res.json();
  const mapped = data.map(v => ({
    code: v.abbreviation.toLowerCase(),
    label: v.version.trim(),
    language: v.language
  }));
  localStorage.setItem(cacheKey, JSON.stringify({ data: mapped, expiry: Date.now() + 24 * 60 * 60 * 1000 }));
  return mapped;
}
// IQBible API service for fetching Bible passages and keyword search
// See https://iqbible.com/api-docs/

const API_BASE = 'https://iq-bible.p.rapidapi.com';
const RAPIDAPI_HOST = 'iq-bible.p.rapidapi.com';
const RAPIDAPI_KEY = '3270e78284mshaa01852a7d7edfcp175c02jsn3bb189bea437';

// Helper: Map book name to bookId (01 = Genesis, 43 = John, etc.)
// You may want to keep this in sync with your BIBLE_BOOKS array
const BOOK_NAME_TO_ID = {
  'Genesis': '01', 'Exodus': '02', 'Leviticus': '03', 'Numbers': '04', 'Deuteronomy': '05',
  'Joshua': '06', 'Judges': '07', 'Ruth': '08', '1 Samuel': '09', '2 Samuel': '10',
  '1 Kings': '11', '2 Kings': '12', '1 Chronicles': '13', '2 Chronicles': '14', 'Ezra': '15',
  'Nehemiah': '16', 'Esther': '17', 'Job': '18', 'Psalms': '19', 'Proverbs': '20',
  'Ecclesiastes': '21', 'Song of Solomon': '22', 'Isaiah': '23', 'Jeremiah': '24', 'Lamentations': '25',
  'Ezekiel': '26', 'Daniel': '27', 'Hosea': '28', 'Joel': '29', 'Amos': '30',
  'Obadiah': '31', 'Jonah': '32', 'Micah': '33', 'Nahum': '34', 'Habakkuk': '35',
  'Zephaniah': '36', 'Haggai': '37', 'Zechariah': '38', 'Malachi': '39',
  // New Testament
  'Matthew': '40', 'Mark': '41', 'Luke': '42', 'John': '43', 'Acts': '44',
  'Romans': '45', '1 Corinthians': '46', '2 Corinthians': '47', 'Galatians': '48', 'Ephesians': '49',
  'Philippians': '50', 'Colossians': '51', '1 Thessalonians': '52', '2 Thessalonians': '53', '1 Timothy': '54',
  '2 Timothy': '55', 'Titus': '56', 'Philemon': '57', 'Hebrews': '58', 'James': '59',
  '1 Peter': '60', '2 Peter': '61', '1 John': '62', '2 John': '63', '3 John': '64',
  'Jude': '65', 'Revelation': '66'
};

function getBookId(bookName) {
  return BOOK_NAME_TO_ID[bookName] || '01';
}

function getVersionId(translation) {
  // Map your translation codes to API versionIds
  const map = {
    bsb: 'bsb', kjv: 'kjv', web: 'web', asv: 'asv', niv: 'niv', esv: 'esv', nlt: 'nlt', nasb: 'nasb', csb: 'csb', nkjv: 'nkjv', msg: 'msg'
  };
  return map[translation.toLowerCase()] || 'kjv';
}

function getHeaders() {
  return {
    'x-rapidapi-host': RAPIDAPI_HOST,
    'x-rapidapi-key': RAPIDAPI_KEY
  };
}


// Fetch a chapter (e.g., Genesis 1)
export async function fetchIqbiblePassage(reference, translation = 'kjv') {
  // reference: 'Genesis 1' or 'John 3'
  const [bookName, chapterStr] = reference.split(' ');
  const bookId = getBookId(bookName);
  const chapterId = String(parseInt(chapterStr, 10)).padStart(2, '0');
  const versionId = getVersionId(translation);
  const url = `${API_BASE}/GetChapter?bookId=${bookId}&chapterId=${parseInt(chapterStr, 10)}&versionId=${versionId}`;
  const res = await fetch(url, { headers: getHeaders() });
  if (!res.ok) throw new Error('IQBible API error');
  const data = await res.json();
  // API returns array of verses: {id, b, c, v, t}
  return data.map(v => ({ verse: v.v, text: v.t }));
}


// Keyword search (returns array of matches)
export async function searchIqbibleKeyword(query, translation = 'kjv') {
  const versionId = getVersionId(translation);
  const url = `${API_BASE}/GetSearch?query=${encodeURIComponent(query)}&versionId=${versionId}`;
  const res = await fetch(url, { headers: getHeaders() });
  if (!res.ok) throw new Error('IQBible API error');
  const data = await res.json();
  // API returns array of verses: {id, b, c, v, t}
  return data.map(v => ({ reference: `${v.b} ${v.c}:${v.v}`, text: v.t }));
}
